import React, { useEffect, useState, useMemo } from 'react';
import { ImageFrame, ImageOverlay } from '../ImageOverlay/ImageOverlay';
import HighlightButton, { SecondaryButton } from '../HighlightButton/HighlightButton';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import SidebarMenu from '../SidebarMenu/SidebarMenu';
import { analytics } from '../../firebase';
import { logEvent } from 'firebase/analytics';
import { useProducts } from '../../context/ProductsContext';
import styles from '../../css/Arrangements.module.css';

function Arrangements() {
  const { products, loading, error } = useProducts();
  const [activeSection, setActiveSection] = useState('acappella');

  // Menu items for the sidebar
  const menuItems = useMemo(() => [
    { label: 'A Cappella Arrangements', path: '/work', hash: '#acappella' },
    { label: 'Solo Piano Arrangements', path: '/work', hash: '#piano' },
    { label: 'Transcriptions', path: '/work', hash: '#transcriptions' }
  ], []);

  // Scroll to hash on load if present
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setActiveSection(id);
      }
    }
  }, []);

  // Scroll event listener to detect active section
  useEffect(() => {
    const handleScroll = () => {
      // Extract section IDs from menuItems
      const sections = menuItems
        .filter(item => item.hash)
        .map(item => item.hash.substring(1)); // Remove '#' from hash

      const scrollPosition = window.scrollY + 200; // Offset for better UX

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuItems]);

  const handleClick = (item) => {
    if (analytics) {
      logEvent(analytics, 'click', {
        title: item.title,
        ensemble: item.ensemble,
        composer: item.by,
        year: item.year
      });
    }
  };

  const handleMenuItemClick = (item) => {
    if (analytics) {
      logEvent(analytics, 'menu_click', {
        label: item.label,
        path: item.path + (item.hash || '')
      });
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="mainContainer centeredHorizontallyOnMobile">
        <div className={styles.contentContainer}>
          <LoadingSpinner message="Loading..." />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    console.error('Error fetching products:', error);
    return (
      <div className="mainContainer centeredHorizontallyOnMobile">
        <div className={styles.contentContainer}>
          <p>There was an error loading.</p>
          <div style={{ marginTop: 10 }}>
            <SecondaryButton onClick={() => window.location.reload()}>Retry</SecondaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="centeredHorizontallyOnMobile">
      <div className={styles.contentContainer}>
        <SidebarMenu
          items={menuItems}
          currentPath={window.location.pathname}
          activeSection={activeSection}
          onItemClick={handleMenuItemClick}
        />

        <div>
          <div id="acappella">
            <h1 className={styles.pageTitle}><a href="#acappella" className={styles.titleLink}>A CAPPELLA ARRANGEMENTS</a></h1>

            <div className={styles.cardContainer}>
              {products
                .filter(product => product.type === "arrangement" &&
                  product.ensemble.includes("Cappella") && product.visibility === "Public")
                .map((arrangement) => (
                  <ImageOverlay
                    key={arrangement.id}
                    type="arrangement"
                    item={arrangement}
                    src={`${process.env.PUBLIC_URL}/static/img/coverImages/${arrangement.artfile}`}
                    alt={arrangement.title}
                    onClick={() => handleClick(arrangement)}
                  />
                ))
              }

              <ImageFrame>
                <div className={styles.commissionCard}>
                  <h4 className={styles.ctaText} style={{ paddingBottom: 10 }}>Didn't find anything up your alley?</h4>
                  <HighlightButton
                    onClick={() => {
                      if (analytics) {
                        logEvent(analytics, 'commission_arrangement_button_click');
                      }
                      window.location = "/commissions";
                    }}
                  >
                    COMMISSION ME
                  </HighlightButton>
                </div>
              </ImageFrame>
            </div>
          </div>

          <div id="piano">
            <h1 className={styles.pageTitle}><a href="#piano" className={styles.titleLink}>SOLO PIANO ARRANGEMENTS</a></h1>

            <div className={styles.cardContainer}>
              {products
                .filter(product => product.type === "arrangement" &&
                  product.ensemble === "Solo Piano" && product.visibility === "Public")
                .map((arrangement) => (
                  <ImageOverlay
                    key={arrangement.id}
                    type="arrangement"
                    item={arrangement}
                    src={`${process.env.PUBLIC_URL}/static/img/coverImages/${arrangement.artfile}`}
                    alt={arrangement.title}
                    onClick={() => handleClick(arrangement)}
                  />
                ))
              }

              <ImageFrame>
                <div className={styles.commissionCard}>
                  <h4 className={styles.ctaText} style={{ paddingBottom: 10 }}>Didn't find anything up your alley?</h4>
                  <HighlightButton
                    onClick={() => {
                      if (analytics) {
                        logEvent(analytics, 'commission_arrangement_button_click');
                      }
                      window.location = "/commissions";
                    }}
                  >
                    COMMISSION ME
                  </HighlightButton>
                </div>
              </ImageFrame>
            </div>
          </div>

          <div id="transcriptions">
            <h1 className={styles.pageTitle}><a href="#transcriptions" className={styles.titleLink}>TRANSCRIPTIONS</a></h1>

            <div className={styles.cardContainer}>
              {products
                .filter(product => product.type === "transcription" && product.visibility === "Public")
                .map((transcription) => (
                  <ImageOverlay
                    key={transcription.id}
                    type="transcription"
                    item={transcription}
                    src={`${process.env.PUBLIC_URL}/static/img/coverImages/${transcription.artfile}`}
                    alt={transcription.title}
                    onClick={() => handleClick(transcription)}
                  />
                ))
              }

              <ImageFrame>
                <div className={styles.commissionCard}>
                  <h4 className={styles.ctaText} style={{ paddingBottom: 10 }}>Didn't find anything up your alley?</h4>

                  <HighlightButton
                    onClick={() => {
                      if (analytics) {
                        logEvent(analytics, 'commission_transcription_button_click');
                      }
                      window.location = "/commissions";
                    }}
                  >
                    COMMISSION ME
                  </HighlightButton>
                </div>
              </ImageFrame>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Arrangements;
